import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { autobind } from 'core-decorators';
import RecordRTC from 'recordrtc';
import Draggable from 'react-draggable';
import CircularProgressbar from 'react-circular-progressbar';
import Spinner from 'react-spinkit';

import Counter from './Counter';
import {
  MainContainer,
  VideoContainer,
  Controller,
  Play,
  Pause,
  Record,
  ActionButtons,
  ActionButton,
  Loading,
  RecordIcon,
  RecordStopIcon,
  CircularProgressbarContainer,
} from './MediaComponents';

import * as mediaRecorderActions from '../actions/mediaRecorderActions';
import embedActions from '../actions/embedActions';

import styles from '../styles';
import viewerUtils from '../utils';
import commonUtils from '../../common/utils';
import variables from '../../common/variables';

import smalltalk from '../../vendor/smalltalk';

const canRecord =
  !!(navigator.getUserMedia || navigator.webkitGetUserMedia) &&
  commonUtils.isChrome;

const SPACE_BETWEEN_BUTTONS = 10;
const RECORD_LIMIT = 300;

const VIDEO_PARAMS = {
  audio: true,
  video: {
    width: { min: 240, ideal: 480, max: 720 },
    height: { min: 240, ideal: 480, max: 720 },
    frameRate: { min: 10, ideal: 24, max: 30 },
  },
};

class MediaRecorder extends React.PureComponent {
  constructor(props) {
    super(props);

    const { wh } = this.getPositioningValues();

    this.state = {
      stream: null,
      v: 'bottom',
      h: 'left',
      x: 0,
      y: window.innerHeight - wh,
      dragging: false,
      progress: 0,
      recordStartTime: null,
      isRecording: false,
      isLoading: false,
    };

    this.buttonSize = props.size === 'big' ? 50 : 30;
  }

  componentDidMount() {
    window.addEventListener('resize', this.onWindowResize);
  }

  componentDidUpdate(prevProps) {
    // Stop & cancel everything when changing slides or hiding the recorder
    if (
      this.props.index !== prevProps.index ||
      (prevProps.visibleVideo && !this.props.visibleVideo)
    ) {
      this.pausePreview();
      if (this.props.index !== prevProps.index) {
        this.resetProgress();
      }
      if (this.state.isRecording) {
        this.stopRecord(prevProps.slide.id);
      } else {
        this.stopStream();
      }
    }

    if (this.props.size !== prevProps.size) {
      this.buttonSize = this.props.size === 'big' ? 50 : 30;
      this.repositionWithSize();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  @autobind
  onWindowResize() {
    this.repositionWithSize();
  }

  @autobind
  onDragStart() {
    this.setState({ dragging: true });
  }

  @autobind
  onDragStop(e, position) {
    const { wh, topOffset } = this.getPositioningValues();
    this.setState({ dragging: false });
    if (position.x + wh / 2 < window.innerWidth / 2) {
      this.setState({ h: 'left', x: 0 });
    } else {
      this.setState({ h: 'right', x: window.innerWidth - wh });
    }
    if (position.y + wh / 2 < window.innerHeight / 2) {
      this.setState({ v: 'top', y: topOffset });
    } else {
      this.setState({ v: 'bottom', y: window.innerHeight - wh });
    }
  }

  @autobind
  onRecordTimeUpdate() {
    if (!this.state.isRecording || !this.state.recordStartTime) {
      return;
    }
    const duration = new Date().getTime() / 1000 - this.state.recordStartTime;
    if (duration > RECORD_LIMIT) {
      this.stopRecord();
    } else {
      this.setState({
        progress: Math.min((duration / RECORD_LIMIT) * 100, 100),
      });
    }
  }

  @autobind
  onStopRecordingClick() {
    this.stopRecord();
  }

  @autobind
  onPreviewPause() {
    this.updatePlayingState();
  }

  @autobind
  onPreviewEnded() {
    this.updatePlayingState();
  }

  @autobind
  onPreviewTimeUpdate() {
    this.setState({
      isPlaying: this.preview && this.preview.playing,
      progress: (this.preview.currentTime / this.preview.duration) * 100,
    });
  }

  @autobind
  getPositioningValues() {
    const wh = this.props.size === 'big' ? 260 : 160;
    let topOffset;

    if (this.props.controlsVisible) {
      topOffset = 38;
    } else {
      topOffset = 0;
    }

    return { wh, topOffset };
  }

  updatePlayingState() {
    this.setState({ isPlaying: this.preview && this.preview.playing });
  }

  @autobind
  resetProgress() {
    this.setState({ progress: 0, isPlaying: false });
  }

  @autobind
  startStream() {
    if (canRecord) {
      navigator.mediaDevices
        .getUserMedia(VIDEO_PARAMS)
        .then(stream => {
          this.setState({ stream });
        })
        .catch(err => {
          console.error(err);
          smalltalk.alert('Oops', 'Could not initiate webcam recording.');
        });
    } else {
      smalltalk.alert(
        'Oops',
        'The record feature is still experimental and only works with Chrome for now.',
      );
    }
  }

  @autobind
  stopStream() {
    if (!this.state.stream) {
      return;
    }
    this.state.stream.getTracks().forEach(track => track.stop());
    this.setState({ stream: null });
  }

  @autobind
  startRecord() {
    if (!this.state.stream) {
      return;
    }
    this.setState({
      isRecording: true,
      recordStartTime: new Date().getTime() / 1000,
    });
    this.recordVideo = RecordRTC(this.state.stream, {
      type: 'video',
      mimeType: 'video/webm;codecs=vp9',
    });
    this.recordVideo.startRecording();
  }

  @autobind
  stopRecord(slideId = this.props.slide.id) {
    this.setState({ isRecording: false, recordStartTime: null, progress: 0 });
    this.startLoading();
    this.recordVideo.stopRecording(() => {
      this.stopStream();
      this.props.actions
        .addMediaToSlide(this.recordVideo.blob, slideId)
        .then(() => {
          this.stopLoading();
        })
        .catch(() => {
          smalltalk.alert('Oops', 'There was an error saving your video.');
          this.stopLoading();
        });
    });
  }

  @autobind
  cancelRecord() {
    this.setState({ isRecording: false, recordStartTime: null, progress: 0 });
    this.recordVideo.stopRecording(() => {
      this.stopStream();
    });
  }

  @autobind
  deleteRecord() {
    smalltalk
      .confirm(
        'Delete',
        'Are you sure you want to delete? This will permanently erase what you previously recorded.',
      )
      .then(() => {
        this.resetProgress();
        this.startLoading();
        this.props.actions
          .removeMediaFromSlide(this.props.slide.id)
          .then(() => {
            this.stopLoading();
          });
      })
      .catch(() => {});
  }

  @autobind
  recordAgain() {
    if (!canRecord) {
      return;
    }
    smalltalk
      .confirm(
        'Record again',
        'Are you sure you want to record again? This will permanently erase what you previously recorded.',
      )
      .then(() => {
        this.resetProgress();
        this.startLoading();
        this.props.actions
          .removeMediaFromSlide(this.props.slide.id)
          .then(() => {
            this.stopLoading();
            this.startStream();
          });
      })
      .catch(() => {});
  }

  @autobind
  startLoading() {
    this.setState({ isLoading: true });
  }

  @autobind
  stopLoading() {
    this.setState({ isLoading: false });
  }

  @autobind
  toggleStartStop() {
    const hasMedia = !!this.props.slide.media;
    if (hasMedia) {
      this.togglePreviewPlaying();
    } else if (!this.state.isRecording && !this.state.isLoading) {
      if (this.state.stream) {
        this.stopStream();
      } else {
        this.startStream();
      }
    } else if (this.state.isRecording) {
      this.stopRecord();
    }
  }

  @autobind
  togglePreviewPlaying() {
    if (this.state.isPlaying) {
      this.pausePreview();
    } else {
      this.playPreview();
    }
  }

  @autobind
  playPreview() {
    if (!this.preview) {
      return;
    }
    // Modern browsers receive a promise when calling play()
    // and must handle it properly
    // https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
    const playPromise = this.preview.play();
    if (playPromise) {
      playPromise
        .then(() => {
          // Playback started
        })
        .catch(() => {
          // Playback was prevented
        });
    }
  }

  @autobind
  pausePreview() {
    if (!this.preview) {
      return;
    }
    this.preview.pause();
  }

  @autobind
  repositionWithSize() {
    const { wh, topOffset } = this.getPositioningValues();

    if (this.state.h === 'left') {
      this.setState({ x: 0 });
    } else {
      this.setState({ x: window.innerWidth - wh });
    }
    if (this.state.v === 'top') {
      this.setState({ y: topOffset });
    } else {
      this.setState({ y: window.innerHeight - wh });
    }
  }

  @autobind
  renderLoading() {
    return (
      <Draggable
        disabled
        position={{
          x: this.state.x,
          y: this.state.y,
        }}
      >
        <MainContainer
          size={this.props.size}
          visible={
            this.props.visibleVideo &&
            !(this.props.slidesShowed && this.state.h === 'left')
          }
        >
          <CircularProgressbarContainer border />
          <Loading className="loading-container">
            <Spinner fadeIn="none" name="double-bounce" />
          </Loading>
        </MainContainer>
      </Draggable>
    );
  }

  @autobind
  renderMediaPreviewer() {
    const url = this.props.slide.media.remoteUrl;
    const urlWithoutExtension = url.substr(0, url.lastIndexOf('.'));

    const R = this.props.size === 'big' ? 100 : 50;

    const y1 = R - (this.buttonSize + SPACE_BETWEEN_BUTTONS);
    const y2 = R;
    const y3 = R + (this.buttonSize + SPACE_BETWEEN_BUTTONS);
    const x1 = viewerUtils.getXPositionMediaButton('left', R, y1);
    const x2 = viewerUtils.getXPositionMediaButton('left', R, y2);
    const x3 = viewerUtils.getXPositionMediaButton('left', R, y3);

    const dragButtonPosition = {};
    if (this.state.h === 'left') {
      dragButtonPosition.left = x1;
    } else {
      dragButtonPosition.right = x1;
    }

    const recordButtonPosition = {};
    if (this.state.h === 'left') {
      recordButtonPosition.left = x2;
    } else {
      recordButtonPosition.right = x2;
    }

    const deleteButtonPosition = {};
    if (this.state.h === 'left') {
      deleteButtonPosition.left = x3;
    } else {
      deleteButtonPosition.right = x3;
    }

    const progressBarStyles = {
      path: { stroke: variables.colors.blue },
      trail: { stroke: variables.colors.white },
    };
    if (this.state.progress === 0) {
      progressBarStyles.path.transition = 'none';
    }

    return (
      <Draggable
        onDrag={this.onDragStart}
        onStop={this.onDragStop}
        handle=".button-handle"
        position={{
          x: this.state.x,
          y: this.state.y,
        }}
      >
        <MainContainer
          size={this.props.size}
          visible={
            this.props.visibleVideo &&
            !(this.props.slidesShowed && this.state.h === 'left')
          }
          dragging={this.state.dragging}
        >
          <CircularProgressbarContainer>
            <CircularProgressbar
              percentage={this.state.progress}
              strokeWidth={this.props.size === 'big' ? 2.5 : 5}
              textForPercentage=""
              styles={progressBarStyles}
            />
          </CircularProgressbarContainer>
          <VideoContainer>
            <video
              ref={el => {
                this.preview = el;
              }}
              src={`${urlWithoutExtension.replace(
                '/video/upload/',
                '/video/upload/vc_auto/',
              )}.mp4`}
              style={styles.mediaVideo}
              playsInline
              onPause={this.onPreviewPause}
              onEnded={this.onPreviewEnded}
              onTimeUpdate={this.onPreviewTimeUpdate}
            />
            {!this.state.isPlaying && this.state.progress === 0 && (
              <img
                src={`${urlWithoutExtension.replace(
                  '/video/upload/',
                  '/video/upload/so_0/',
                )}.jpg`}
                style={{ ...styles.mediaVideoPoster }}
                alt=""
              />
            )}
            <Controller>
              {this.state.isPlaying ? (
                <Pause onClick={this.pausePreview} size={this.props.size} />
              ) : (
                <Play onClick={this.playPreview} size={this.props.size} />
              )}
            </Controller>
          </VideoContainer>
          <ActionButtons>
            <ActionButton
              onClick={this.deleteRecord}
              bottom={y1}
              size={this.buttonSize}
              {...dragButtonPosition}
            >
              <i className="icon-delete" />
              <span>Delete</span>
            </ActionButton>
            <ActionButton
              onClick={this.recordAgain}
              bottom={y2}
              color="black"
              hoverColor="black"
              backgroundColor="white"
              backgroundHoverColor="white"
              size={this.buttonSize}
              {...recordButtonPosition}
            >
              <RecordIcon size={this.buttonSize} />
              <span>Record again</span>
            </ActionButton>
            <ActionButton
              className="button-handle"
              cursor="move"
              bottom={y3}
              size={this.buttonSize}
              {...deleteButtonPosition}
            >
              <i className="icon-hand" />
              <span>Drag</span>
            </ActionButton>
          </ActionButtons>
        </MainContainer>
      </Draggable>
    );
  }

  @autobind
  renderMediaRecorder() {
    const R = this.props.size === 'big' ? 100 : 50;
    let y1;
    let x2;
    let y2;

    if (this.state.isRecording) {
      y1 = R + (this.buttonSize + SPACE_BETWEEN_BUTTONS) / 2;
      y2 = R - (this.buttonSize + SPACE_BETWEEN_BUTTONS) / 2;
      x2 = viewerUtils.getXPositionMediaButton('left', R, y2);
    } else {
      y1 = R;
    }

    const x1 = viewerUtils.getXPositionMediaButton('left', R, y1);

    const dragButtonPosition = {};
    if (this.state.h === 'left') {
      dragButtonPosition.left = x1;
    } else {
      dragButtonPosition.right = x1;
    }

    const recordButtonPosition = {};
    if (this.state.h === 'left') {
      recordButtonPosition.left = x2;
    } else {
      recordButtonPosition.right = x2;
    }

    return (
      <Draggable
        onDrag={this.onDragStart}
        onStop={this.onDragStop}
        handle=".button-handle"
        position={{
          x: this.state.x,
          y: this.state.y,
        }}
      >
        <MainContainer
          size={this.props.size}
          visible={
            this.props.visibleVideo &&
            !(this.props.slidesShowed && this.state.h === 'left')
          }
          dragging={this.state.dragging}
        >
          <CircularProgressbarContainer border={!this.state.isRecording}>
            {this.state.isRecording && (
              <CircularProgressbar
                percentage={this.state.progress}
                strokeWidth={this.props.size === 'big' ? 2.5 : 5}
                textForPercentage=""
                styles={{
                  path: { stroke: variables.colors.blue },
                  trail: { stroke: variables.colors.white },
                }}
              />
            )}
          </CircularProgressbarContainer>
          <VideoContainer>
            {this.state.stream ? (
              <video
                style={{
                  ...styles.mediaVideo,
                  transform: 'scaleX(-1)',
                  opacity: this.state.isRecording ? 1 : 0.5,
                }}
                autoPlay
                muted
                playsInline
                ref={el => {
                  this.record = el;
                  if (this.record) {
                    if ('srcObject' in this.record) {
                      if (this.record.srcObject !== this.state.stream) {
                        this.record.srcObject = this.state.stream;
                      }
                    } else {
                      // Avoid using this in new browsers, as it is going away.
                      this.record.src = window.URL.createObjectURL(
                        this.state.stream,
                      );
                    }
                  }
                }}
                onTimeUpdate={this.onRecordTimeUpdate}
              />
            ) : (
              <Controller initialOpacity={0.8}>
                <Record onClick={this.startStream} size={this.props.size} />
              </Controller>
            )}
          </VideoContainer>
          {!this.state.isRecording && this.state.stream && (
            <Counter
              val={3}
              onClick={this.stopStream}
              onEnd={this.startRecord}
            />
          )}
          <ActionButtons>
            <ActionButton
              className="button-handle"
              cursor="move"
              bottom={y1}
              size={this.buttonSize}
              {...dragButtonPosition}
            >
              <i className="icon-hand" />
              <span>Drag</span>
            </ActionButton>
            {this.state.isRecording && (
              <ActionButton
                onClick={this.onStopRecordingClick}
                bottom={y2}
                color="black"
                hoverColor="black"
                backgroundColor="white"
                backgroundHoverColor="white"
                size={this.buttonSize}
                {...recordButtonPosition}
              >
                <RecordStopIcon size={this.buttonSize} />
                <span>Stop recording</span>
              </ActionButton>
            )}
          </ActionButtons>
        </MainContainer>
      </Draggable>
    );
  }

  renderMedia() {
    return this.props.slide.media
      ? this.renderMediaPreviewer()
      : this.renderMediaRecorder();
  }

  render() {
    return this.state.isLoading ? this.renderLoading() : this.renderMedia();
  }
}

MediaRecorder.propTypes = {
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  index: PropTypes.number.isRequired,
  slide: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.objectOf(PropTypes.any).isRequired,
    media: PropTypes.objectOf(PropTypes.any),
  }).isRequired,
  size: PropTypes.string.isRequired,
  visibleVideo: PropTypes.bool.isRequired,
  controlsVisible: PropTypes.bool.isRequired,
  slidesShowed: PropTypes.bool.isRequired,
};

MediaRecorder.defaultProps = {};

const mapStateToProps = state => ({
  visibleVideo: state.mediaRecorderReducer.visibleVideo,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...mediaRecorderActions,
      ...embedActions,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(MediaRecorder);
